import type { Sheet } from "@fortune-sheet/core";
import { useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { backendService } from "@/services/backend.service";
import { hydrateArrayBufferError } from "@/utils/arrayBufferErrorDetail";
import { excelBufferToFortuneSheet } from "@/utils/excelToFortuneSheet";
import type { PersistedMonthlyExportSettings } from "./types";

/** The backend's own key for "this department has no shift history to derive shift windows from". */
const NO_SCHEDULE_HISTORY_KEY = "error.no.schedule.history";
const MONTHLY_STALE_TIME_MS = 2 * 60 * 1000;

const FILE_DATE_FORMAT = "yyyy-MM-dd";

/** Stable identity, so an errored/idle query doesn't hand the preview a fresh array every render. */
const EMPTY_SHEETS: Sheet[] = [];

// `wrapText` and `sortBy` are both omitted, not just unused: they are vestigial in the stored
// document (every cell wraps now; `sortBy` is the deprecated legacy field `sortEntries` replaced —
// see its comment in types.ts), and taking either would put it back in the query key — refetching
// the same workbook for a value that never meaningfully changes.
type Params = Omit<PersistedMonthlyExportSettings, "wrapText" | "sortBy"> & {
	departmentId: string;
	startDate: DateTime;
	endDate: DateTime;
	showPtoSheet: boolean;
	/** False while the popup is closed or the weekly tab is active, so nothing is fetched unseen. */
	enabled: boolean;
};

/**
 * Fetch the monthly schedule workbook once and hand back both the bytes and the parsed sheets.
 *
 * One request serves the preview AND the download, so what the user approves is byte-for-byte what
 * they get, and picking a format costs no second round trip.
 */
export const useMonthlyScheduleExportPreview = ({
	departmentId,
	startDate,
	endDate,
	sortEntries,
	shiftTimeSeparation,
	shiftCellDisplay,
	printGaps,
	showHireDate,
	showPtoSheet,
	recurringOnly,
	hideAgency,
	orientation,
	fitToPage,
	fitToWidth,
	fitToHeight,
	columnWidths,
	enabled,
}: Params) => {
	const { t } = useTranslation();
	const startDateStr = startDate.toFormat(FILE_DATE_FORMAT);
	const endDateStr = endDate.toFormat(FILE_DATE_FORMAT);

	const query = useQuery<{ buffer: ArrayBuffer; sheets: Sheet[] }>({
		// Every input that IDENTIFIES the workbook. A setting missing here does not fail loudly — it
		// serves the PREVIOUS workbook, which presents as a caching bug rather than a missing parameter,
		// so `useMonthlyScheduleExportPreview.spec.tsx` asserts one entry per setting.
		//
		// `columnWidths` is deliberately ABSENT. Widths are a parameter the workbook is built with, not
		// part of what makes it a different workbook, and a column drag emits a change per pointer move —
		// keying on them refetched the whole workbook on every step of a drag and left a cache entry
		// behind for each intermediate width. The owner refetches once when a drag settles instead.
		queryKey: [
			"monthly-schedule-export",
			departmentId,
			startDateStr,
			endDateStr,
			sortEntries,
			shiftTimeSeparation,
			shiftCellDisplay,
			printGaps,
			showHireDate,
			showPtoSheet,
			recurringOnly,
			hideAgency,
			orientation,
			fitToPage,
			fitToWidth,
			fitToHeight,
		],
		enabled,
		retry: false,
		// Without these the whole workbook refetched on every window refocus: the sheet remounted, the
		// download went dead mid-look, and a failed refetch replaced a good preview with an error.
		staleTime: MONTHLY_STALE_TIME_MS,
		refetchOnWindowFocus: false,
		queryFn: async () => {
			try {
				const response = await backendService.getMonthlyScheduleDownload({
					departmentId,
					startDate: startDateStr,
					endDate: endDateStr,
					sortEntries,
					shiftTimeSeparation,
					shiftCellDisplay,
					printGaps,
					showHireDate,
					showPtoSheet,
					recurringOnly,
					hideAgency,
					orientation,
					fitToPage,
					fitToWidth,
					fitToHeight,
					columnWidths,
				});
				const buffer = response.data as ArrayBuffer;
				// Always: every cell of the workbook wraps, and the backend sizes every row to the lines
				// it wrapped to — the vertical space the converter needs before it carries wrap mode over.
				const sheets = await excelBufferToFortuneSheet(buffer, columnWidths, { applyWrapText: true });
				return { buffer, sheets };
			} catch (error) {
				// The request asks for an arraybuffer, so a failure's JSON body arrives as binary and
				// every `error.response.data.detail` reader downstream sees nothing. Decode it back in
				// place before anyone tries to render a reason.
				hydrateArrayBufferError(error);
				throw error;
			}
		},
	});

	// Cast rather than a type guard: axios errors are `unknown` to react-query, and the shape asserted
	// here is exactly what `hydrateArrayBufferError` writes back onto the error above. Every hop is
	// optional-chained, so a different shape yields `undefined` rather than throwing.
	const detailMessage = (query.error as { response?: { data?: { detail?: { message?: string } } } } | null)?.response?.data?.detail?.message;
	const isMissingScheduleHistory = detailMessage === NO_SCHEDULE_HISTORY_KEY;

	return {
		buffer: query.data?.buffer,
		sheets: query.data?.sheets ?? EMPTY_SHEETS,
		isLoading: query.isFetching,
		/** Already-translated, user-facing reason the preview is empty. `undefined` when healthy. */
		errorMessage: query.error ? (detailMessage ? t(detailMessage) : t("download.preview.error")) : undefined,
		/** The one failure that has its own guidance rather than a generic "couldn't generate". */
		isMissingScheduleHistory,
		refetch: query.refetch,
	};
};
